import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ChevronLeft, UserPlus, Edit2, Trash2, Key, Users, ShieldCheck, User, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from '../components/ConfirmModal';
import { SkeletonBox } from '../components/Skeleton';

function Modal({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-sm" style={{ padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const EMPTY_USER = { nama: '', username: '', email: '', password: '', role: 'user', is_active: 1 };

const ACTION_LABELS = { CREATE: 'Tambah', UPDATE: 'Ubah', DELETE: 'Hapus', LOGIN: 'Login', LOGOUT: 'Logout' };
const ACTION_COLORS = { CREATE: 'badge-green', UPDATE: 'badge-blue', DELETE: 'badge-red', LOGIN: 'badge-gray', LOGOUT: 'badge-gray' };

export default function UsersPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [newPass, setNewPass] = useState('');
  const [targetUserId, setTargetUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = () => {
    setLoading(true);
    api.get('/users').then(res => setUsers(res.data.data)).catch(() => toast.error('Gagal memuat data user')).finally(() => setLoading(false));
  };

  const fetchAuditLog = (p = 1) => {
    setLoadingAudit(true);
    api.get('/users/audit-log', { params: { page: p, limit: 20 } })
      .then(res => { setAuditLog(res.data.data); setAuditTotal(res.data.total); setAuditTotalPages(res.data.totalPages); })
      .catch(() => toast.error('Gagal memuat audit log'))
      .finally(() => setLoadingAudit(false));
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { if (tab === 'audit') fetchAuditLog(auditPage); }, [tab, auditPage]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const openCreate = () => { setEditingUser(null); setForm(EMPTY_USER); setShowModal(true); };
  const openEdit = (u) => { setEditingUser(u); setForm({ nama: u.nama, username: u.username, email: u.email, password: '', role: u.role, is_active: u.is_active }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, { nama: form.nama, email: form.email, role: form.role, is_active: form.is_active });
        toast.success('User diperbarui');
      } else {
        await api.post('/users', form);
        toast.success('User berhasil ditambahkan');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success('User dihapus');
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menghapus');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPass = async (e) => {
    e.preventDefault();
    if (!newPass) return;
    setSaving(true);
    try {
      await api.put(`/users/${targetUserId}/reset-password`, { newPassword: newPass });
      toast.success('Password berhasil direset');
      setShowPassModal(false);
      setNewPass('');
    } catch { toast.error('Gagal reset password'); } finally { setSaving(false); }
  };

  return (
    <div className="page-container">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate('/')}>
        <ChevronLeft size={16} /> Kembali
      </button>

      <div style={{ background: 'linear-gradient(135deg, var(--gray-800), var(--gray-700))', borderRadius: 'var(--radius-lg)', padding: '28px 32px', color: 'white', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800 }}>Kelola Pengguna</h1>
          <p style={{ opacity: 0.7, fontSize: 14, marginTop: 4 }}>Manajemen akun dan audit log sistem</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><UserPlus size={16} /> Tambah User</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--gray-100)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[{ key: 'users', label: 'Pengguna', icon: <Users size={15} /> }, { key: 'audit', label: 'Audit Log', icon: <Activity size={15} /> }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
            borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 14, fontWeight: 600, transition: 'all 0.15s',
            background: tab === t.key ? 'white' : 'transparent',
            color: tab === t.key ? 'var(--gray-900)' : 'var(--gray-500)',
            boxShadow: tab === t.key ? 'var(--shadow-sm)' : 'none',
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      {tab === 'users' && (
        <>
          <div className="grid-2" style={{ marginBottom: 24 }}>
            <div className="stat-card">
              <div><div className="stat-label">Total Pengguna</div><div className="stat-value">{users.length}</div></div>
              <div className="stat-icon" style={{ background: 'var(--primary-bg)' }}><Users size={24} color="var(--primary)" /></div>
            </div>
            <div className="stat-card">
              <div><div className="stat-label">Administrator</div><div className="stat-value">{users.filter(u => u.role === 'admin').length}</div></div>
              <div className="stat-icon" style={{ background: 'var(--orange-bg)' }}><ShieldCheck size={24} color="var(--orange)" /></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><div style={{ fontWeight: 700 }}>Daftar Pengguna</div></div>
            <div className="table-container">
              {loading ? (
                <div style={{ padding: 24 }}>{Array(3).fill(0).map((_, i) => <div key={i} style={{ padding: '14px 0', borderBottom: '1px solid var(--gray-100)' }}><SkeletonBox height={14} width={300} /></div>)}</div>
              ) : (
                <table>
                  <thead>
                    <tr><th>NAMA</th><th>USERNAME</th><th>EMAIL</th><th>ROLE</th><th>STATUS</th><th>AKSI</th></tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: u.role === 'admin' ? 'var(--orange-bg)' : 'var(--primary-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: u.role === 'admin' ? 'var(--orange)' : 'var(--primary)' }}>
                              {u.nama[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{u.nama}</div>
                              {u.id === currentUser?.id && <span className="badge badge-blue" style={{ fontSize: 10 }}>Akun Anda</span>}
                            </div>
                          </div>
                        </td>
                        <td><code style={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}>{u.username}</code></td>
                        <td style={{ fontSize: 13 }}>{u.email}</td>
                        <td><span className={`badge ${u.role === 'admin' ? 'badge-orange' : 'badge-blue'}`}>{u.role === 'admin' ? <><ShieldCheck size={11} /> Admin</> : <><User size={11} /> Staff</>}</span></td>
                        <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Aktif' : 'Nonaktif'}</span></td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '5px 8px' }} onClick={() => openEdit(u)}><Edit2 size={13} /></button>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '5px 8px', color: 'var(--primary)' }} onClick={() => { setTargetUserId(u.id); setNewPass(''); setShowPassModal(true); }}><Key size={13} /></button>
                            {u.id !== currentUser?.id && <button className="btn btn-ghost btn-sm" style={{ padding: '5px 8px', color: 'var(--red)' }} onClick={() => setDeleteTarget(u)}><Trash2 size={13} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'audit' && (
        <div className="card">
          <div className="card-header"><div style={{ fontWeight: 700 }}>Audit Log Sistem</div><div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{auditTotal} entri</div></div>
          <div className="table-container">
            <table>
              <thead><tr><th>WAKTU</th><th>USER</th><th>AKSI</th><th>ENTITAS</th><th>DETAIL</th><th>IP</th></tr></thead>
              <tbody>
                {loadingAudit ? Array(5).fill(0).map((_, i) => <tr key={i}><td colSpan={6}><SkeletonBox height={14} /></td></tr>) :
                  auditLog.length === 0 ? <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Belum ada log</td></tr> :
                  auditLog.map(log => (
                    <tr key={log.id}>
                      <td style={{ fontSize: 12, fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap' }}>{new Date(log.created_at).toLocaleString('id-ID')}</td>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{log.user_nama || '—'}</td>
                      <td><span className={`badge ${ACTION_COLORS[log.action] || 'badge-gray'}`}>{ACTION_LABELS[log.action] || log.action}</span></td>
                      <td style={{ fontSize: 13 }}>{log.entity} {log.entity_id ? `#${log.entity_id}` : ''}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-500)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {typeof log.detail === 'string' ? log.detail : JSON.stringify(log.detail)}
                      </td>
                      <td style={{ fontSize: 12, fontFamily: 'JetBrains Mono', color: 'var(--gray-400)' }}>{log.ip_address || '—'}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
          {auditTotalPages > 1 && (
            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>Hal {auditPage} / {auditTotalPages}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => setAuditPage(p => p-1)} disabled={auditPage <= 1}>← Sebelumnya</button>
                <button className="btn btn-outline btn-sm" onClick={() => setAuditPage(p => p+1)} disabled={auditPage >= auditTotalPages}>Berikutnya →</button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal show={showModal} onClose={() => setShowModal(false)} title={editingUser ? 'Edit Pengguna' : 'Tambah Pengguna'}>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group"><label className="form-label">Nama Lengkap</label><input className="form-input" type="text" value={form.nama} onChange={set('nama')} required /></div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Username</label><input className="form-input" type="text" value={form.username} onChange={set('username')} required disabled={!!editingUser} /></div>
              <div className="form-group"><label className="form-label">Role</label><select className="form-input" value={form.role} onChange={set('role')}><option value="user">Staff</option><option value="admin">Administrator</option></select></div>
            </div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={set('email')} required /></div>
            {!editingUser && <div className="form-group"><label className="form-label">Password</label><input className="form-input" type="password" value={form.password} onChange={set('password')} required minLength={6} /></div>}
            {editingUser && <div className="form-group"><label className="form-label">Status</label><select className="form-input" value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: Number(e.target.value) }))}><option value={1}>Aktif</option><option value={0}>Nonaktif</option></select></div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Batal</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="loader" /> : null} Simpan</button>
          </div>
        </form>
      </Modal>

      <Modal show={showPassModal} onClose={() => setShowPassModal(false)} title="Reset Password">
        <form onSubmit={handleResetPass}>
          <div className="modal-body"><div className="form-group"><label className="form-label">Password Baru</label><input className="form-input" type="password" value={newPass} onChange={e => setNewPass(e.target.value)} required minLength={6} placeholder="Minimal 6 karakter" /></div></div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={() => setShowPassModal(false)}>Batal</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="loader" /> : <Key size={14} />} Reset Password</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal show={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting} title="Hapus Pengguna?" message={`Akun "${deleteTarget?.nama}" akan dihapus permanen dari sistem.`} confirmLabel="Ya, Hapus" />
    </div>
  );
}
